import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Divider,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  Create as CreateIcon,
  Edit as EditIcon2,
  Delete as DeleteIcon2,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';

function PermissionMatrixTab() {
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [tempMatrix, setTempMatrix] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const loadMatrix = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('tkn');
      const response = await fetch('/api/v1/permission-matrix/ui', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMatrixData(data);
        setTempMatrix(JSON.parse(JSON.stringify(data))); // Deep copy for editing
        console.log('✅ Successfully loaded permission matrix');
      } else {
        setError('Failed to load permission matrix');
        console.error('❌ Load matrix failed:', response.status);
      }
    } catch (err) {
      setError('Error loading permission matrix');
      console.error('❌ Load matrix error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatrix();
  }, []);

  const handlePermissionToggle = (roleName, featureName, permission) => {
    if (!editMode) return;

    setTempMatrix((prev) => {
      const newMatrix = { ...prev };
      const rolePermissions = newMatrix.matrix[roleName] || {};
      const featurePermissions = rolePermissions[featureName] || [];

      const permissionIndex = featurePermissions.indexOf(permission);
      if (permissionIndex > -1) {
        featurePermissions.splice(permissionIndex, 1);
      } else {
        featurePermissions.push(permission);
      }

      rolePermissions[featureName] = featurePermissions;
      newMatrix.matrix[roleName] = rolePermissions;

      return newMatrix;
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('tkn');
      const response = await fetch('/api/v1/permission-matrix/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ matrix: tempMatrix.matrix }),
      });

      if (response.ok) {
        setMatrixData(tempMatrix);
        setEditMode(false);
        setSuccess('Permission matrix updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        console.log('✅ Successfully updated permission matrix');
      } else {
        setError('Failed to update permission matrix');
        console.error('❌ Update matrix failed:', response.status);
      }
    } catch (err) {
      setError('Error updating permission matrix');
      console.error('❌ Update matrix error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setTempMatrix(JSON.parse(JSON.stringify(matrixData)));
    setEditMode(false);
    setError('');
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'view': return <ViewIcon fontSize="small" />;
      case 'create': return <CreateIcon fontSize="small" />;
      case 'edit': return <EditIcon2 fontSize="small" />;
      case 'delete': return <DeleteIcon2 fontSize="small" />;
      case 'admin': return <AdminIcon fontSize="small" />;
      default: return null;
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'view': return 'primary';
      case 'create': return 'success';
      case 'edit': return 'warning';
      case 'delete': return 'error';
      case 'admin': return 'secondary';
      default: return 'default';
    }
  };

  const filterFeaturesByCategory = (features) => {
    if (selectedCategory === 'all') return features;
    return features.filter((feature) => feature.category === selectedCategory);
  };

  const getCategoryColor = (category) => {
    const colors = {
      core: 'primary',
      ai: 'secondary',
      forms: 'success',
      analytics: 'info',
      security: 'warning',
      administration: 'error',
      custom: 'default',
    };
    return colors[category] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading permission matrix...</Typography>
      </Box>
    );
  }

  if (!matrixData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load permission matrix</Alert>
      </Box>
    );
  }

  const filteredFeatures = filterFeaturesByCategory(matrixData.features);
  const categories = [...new Set(matrixData.features.map((f) => f.category))];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h5">Permission Matrix</Typography>
        <Box>
          {editMode ? (
            <>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={handleSave}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                Save
              </Button>
              <Button
                startIcon={<CancelIcon />}
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              startIcon={<EditIcon />}
              variant="contained"
              onClick={() => setEditMode(true)}
            >
              Edit Matrix
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Category Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Filter by Category:</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label="All"
            color={selectedCategory === 'all' ? 'primary' : 'default'}
            onClick={() => setSelectedCategory('all')}
            clickable
          />
          {categories.map((category) => (
            <Chip
              key={category}
              label={category.charAt(0).toUpperCase() + category.slice(1)}
              color={selectedCategory === category ? 'primary' : 'default'}
              onClick={() => setSelectedCategory(category)}
              clickable
            />
          ))}
        </Box>
      </Box>

      {/* Permission Matrix Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>Feature</TableCell>
              {matrixData.roles.map((role) => (
                <TableCell key={role.name} align="center" sx={{ minWidth: 120 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {role.displayName}
                    </Typography>
                    <Chip
                      label={role.category}
                      size="small"
                      color={role.isSystem ? 'primary' : 'default'}
                      variant={role.isSystem ? 'filled' : 'outlined'}
                    />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFeatures.map((feature) => (
              <TableRow key={feature.name}>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {feature.displayName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {feature.description}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={feature.category}
                        size="small"
                        color={getCategoryColor(feature.category)}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </TableCell>
                {matrixData.roles.map((role) => (
                  <TableCell key={`${feature.name}-${role.name}`} align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {['view', 'create', 'edit', 'delete', 'admin'].map((permission) => {
                        const hasPermission = tempMatrix.matrix[role.name]?.[feature.name]?.includes(permission);
                        return (
                          <Tooltip
                            key={permission}
                            title={`${permission.charAt(0).toUpperCase() + permission.slice(1)} ${feature.displayName}`}
                          >
                            <Box>
                              {editMode ? (
                                <FormControlLabel
                                  control={(
                                    <Switch
                                      checked={hasPermission}
                                      onChange={() => handlePermissionToggle(role.name, feature.name, permission)}
                                      size="small"
                                      color={getPermissionColor(permission)}
                                    />
                                  )}
                                  label=""
                                  sx={{ m: 0 }}
                                />
                              ) : (
                                <Chip
                                  icon={getPermissionIcon(permission)}
                                  label={permission}
                                  size="small"
                                  color={hasPermission ? getPermissionColor(permission) : 'default'}
                                  variant={hasPermission ? 'filled' : 'outlined'}
                                  sx={{ minWidth: 60 }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Permission Legend:</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {['view', 'create', 'edit', 'delete', 'admin'].map((permission) => (
            <Box key={permission} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={getPermissionIcon(permission)}
                label={permission}
                size="small"
                color={getPermissionColor(permission)}
              />
              <Typography variant="caption">
                {permission.charAt(0).toUpperCase() + permission.slice(1)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Instructions */}
      {editMode && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Editing Mode:</strong>
            {' '}
            Use the switches to toggle permissions for each role and feature.
            Click "Save" to apply changes or "Cancel" to discard changes.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

export default PermissionMatrixTab;
